import styled, { css } from 'styled-components';
import { theme } from '../../theme';

export const PortfolioSection = styled.section`
  background: linear-gradient(180deg, ${theme.colors.bgSecondary} 0%, ${theme.colors.bg} 100%);
  position: relative;
  padding: 5rem 0 6rem;
  border-top: 1px solid rgba(148, 163, 184, 0.08);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(148, 163, 184, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.04) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.4) 15%, transparent 100%);
  }
`;

export const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 1rem;
  }
`;

export const SectionHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 430px);
  gap: 2rem;
  align-items: end;
  margin-bottom: 3rem;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const SectionTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.colors.textSecondary};
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.18);
  margin-bottom: 1rem;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  line-height: 1.08;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.textMuted};
  max-width: 430px;
  margin: 0;
  line-height: 1.75;
`;

export const CasesGrid = styled.div`
  display: grid;
  gap: 1.35rem;
  margin-bottom: 3rem;
`;

export const CaseStudy = styled.article<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: minmax(320px, 430px) 1fr;
  gap: 1rem;
  padding: 1rem;
  border-radius: 30px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(11, 17, 23, 0.92));
  box-shadow: 0 18px 40px rgba(2, 6, 23, 0.2);
  display: grid;

  ${({ $reverse }) =>
    $reverse &&
    css`
      grid-template-columns: 1fr minmax(320px, 430px);

      > *:first-child {
        order: 2;
      }

      > *:last-child {
        order: 1;
      }
    `}

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;

    > *:first-child,
    > *:last-child {
      order: initial;
    }
  }
`;

export const CaseVisual = styled.div`
  min-height: 280px;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: #080d16;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export const CaseBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.4rem 0.35rem 0.4rem 0.15rem;

  @media (max-width: ${theme.breakpoints.lg}) {
    padding: 0.3rem 0.1rem 0;
  }
`;

export const CaseEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-bottom: 0.8rem;
  padding: 0.38rem 0.72rem;
  border-radius: 999px;
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.22);
  background: rgba(${theme.colors.primaryRgb}, 0.08);
  color: ${theme.colors.primaryLight};
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CaseTitle = styled.h3`
  font-size: clamp(1.55rem, 3.5vw, 2.3rem);
  font-weight: 700;
  letter-spacing: -0.05em;
  color: ${theme.colors.text};
  margin-bottom: 0.7rem;
`;

export const CaseDesc = styled.p`
  font-size: 0.96rem;
  color: ${theme.colors.textMuted};
  line-height: 1.75;
  margin-bottom: 1rem;
`;

export const CaseMetaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

export const CaseMetaCard = styled.div`
  min-width: 170px;
  flex: 1 1 170px;
  padding: 0.9rem 0.95rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 12, 18, 0.62);

  span {
    display: block;
    margin-bottom: 0.38rem;
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.colors.textMuted};
  }

  strong {
    font-size: 0.88rem;
    line-height: 1.45;
    color: ${theme.colors.textSecondary};
  }
`;

export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const Tag = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.38rem 0.78rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.colors.textSecondary};
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.14);

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

export const CtaRow = styled.div`
  text-align: center;
`;

export const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.85rem 2rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${theme.colors.text};
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
  transition: ${theme.transitions.default};
  cursor: pointer;

  &:hover {
    border-color: rgba(226, 232, 240, 0.3);
    color: ${theme.colors.text};
    background: rgba(30, 41, 59, 0.92);
    transform: translateY(-2px);
  }
`;
